import React, { useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { v2SettingsApi, type EffectiveResponse } from './v2-settings-api';

const CHAIN_LEVELS = ['customer', 'group', 'team', 'user'] as const;

// Centralizes API-error shape into one user-facing string.
const extractApiError = (err: any): string =>
  err?.response?.data?.error || err?.response?.data?.message || err?.message || 'unknown error';

export const CascadeVisualizer: React.FC = () => {
  const [customerId, setCustomerId] = useState('');
  const [groupId, setGroupId] = useState('');
  const [teamId, setTeamId] = useState('');
  const [userId, setUserId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<EffectiveResponse | null>(null);

  const run = async () => {
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const r = await v2SettingsApi.getEffective({
        customerId: customerId.trim() || undefined,
        groupId: groupId.trim() || undefined,
        teamId: teamId.trim() || undefined,
        userId: userId.trim() || undefined,
      });
      setData(r);
    } catch (err: any) {
      setError(extractApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
        <TextField size="small" label="customerId" value={customerId} onChange={(e) => setCustomerId(e.target.value)} />
        <TextField size="small" label="groupId" value={groupId} onChange={(e) => setGroupId(e.target.value)} />
        <TextField size="small" label="teamId" value={teamId} onChange={(e) => setTeamId(e.target.value)} />
        <TextField size="small" label="userId" value={userId} onChange={(e) => setUserId(e.target.value)} />
        <Button variant="contained" onClick={run} disabled={loading}>Resolve</Button>
      </Stack>

      {loading && <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>}
      {error && <Typography color="error">Error: {error}</Typography>}
      {data && (
        <>
          {data.chain && (
            <Stack direction="row" spacing={1} sx={{ mb: 1 }} flexWrap="wrap" alignItems="center">
              <Typography variant="caption">Chain:</Typography>
              {CHAIN_LEVELS.map((lvl) => {
                const entry = data.chain?.[lvl];
                return entry ? (
                  <Chip
                    key={lvl}
                    size="small"
                    variant={entry.source === 'provided' ? 'filled' : 'outlined'}
                    color={entry.source === 'provided' ? 'primary' : 'default'}
                    label={`${lvl} ${entry.id}${entry.source === 'derived' ? ' (derived)' : ''}`}
                    sx={{ fontFamily: 'monospace' }}
                  />
                ) : (
                  <Chip key={lvl} size="small" variant="outlined" disabled label={`${lvl} —`} />
                );
              })}
            </Stack>
          )}
          <Typography variant="caption" sx={{ mb: 2, display: 'block' }}>
            Resolved {Object.keys(data?.resolved ?? {}).length} settings · environment: <code>{data.environment}</code>
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Setting</TableCell>
                <TableCell>Value</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Lock</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(data?.resolved ?? {})
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([name, r]) => (
                  <TableRow key={name} hover>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{name}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {JSON.stringify(r.value)}
                    </TableCell>
                    <TableCell><Chip size="small" label={r.source} variant="outlined" /></TableCell>
                    <TableCell>
                      {r.lockedBy ? (
                        <Chip size="small" label={`locked by ${r.lockedBy}`} color="warning"
                              title={r.reason || ''} />
                      ) : (
                        <Chip size="small" label="open" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </>
      )}
    </Box>
  );
};
